import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { v4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { MetricsTypeEnum } from '~/backend/types';
import {
    decodeSegmentationMetrics,
    SegmentationData,
} from '~/backend/segmentationMetrics';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { pseudonymizeState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import useAnonymizer from '../useAnonymizer';
import { useMetricsBackend, useSessionID } from '../useBackend';
import { useUploadAndProcessMetrics } from '../useUploadAndProcessMetrics';

const UNREADABLE_RESULT_MESSAGE =
    'Segmentation metrics result could not be read.';
const FILES_FAILED_MESSAGE =
    'Segmentation metrics failed for one or more files.';
const PROCESSING_FAILED_MESSAGE =
    'An error occurred during segmentation metrics processing.';

export interface SegmentationMetricsOptions {
    // Which measurement this is; two running at once never see each other's results.
    readonly scope: string;
    readonly onResult?: (rows: SegmentationData[]) => void;
    readonly onError?: (message: string) => void;
}

export function useSegmentationMetrics({
    scope,
    onResult,
    onError,
}: SegmentationMetricsOptions) {
    const client = useMetricsBackend();
    const { anonymizeBlobs } = useAnonymizer();
    const primaryImage = useSelectedPrimaryImage();
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const { resetSessionID } = useSessionID();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SegmentationData[]>();

    const fail = (message: string) => {
        setLoading(false);
        onError?.(message);
    };

    const handleProcessedFiles = async (files: File[]) => {
        setLoading(false);
        try {
            const [first] = files;
            if (!first) {
                throw new Error('Segmentation metrics returned no result.');
            }
            const rows = decodeSegmentationMetrics(
                JSON.parse(new TextDecoder().decode(await first.arrayBuffer()))
            );
            setData(rows);
            onResult?.(rows);
        } catch (error) {
            handleError(error, UNREADABLE_RESULT_MESSAGE);
            fail(UNREADABLE_RESULT_MESSAGE);
        }
    };

    const upload = useUploadAndProcessMetrics(
        MetricsTypeEnum.SEGMETRICS,
        client,
        {
            onSuccess: (files) => {
                handleProcessedFiles(files);
            },
            onError: (errors) => fail(errors.join(' ')),
            scope,
        }
    );

    // The grid the metrics are evaluated on; the primary scan unless another is given.
    const submit = async (
        testStructureSet: StructureSet,
        referenceStructureSet: StructureSet,
        gridImage: Image3D | undefined = primaryImage
    ) => {
        try {
            setLoading(true);
            // Uploaded and polled on one session: the backend keeps a result per session.
            const runSessionId = resetSessionID();
            const sessionClient = client.withSession(runSessionId);
            const taskUID = `${runSessionId}.${v4().split('-')[0]}`;
            const blobs = [
                {
                    file: referenceStructureSet.dicomStructureSet.getAsDicomBlob(),
                    filename: 'ground_truth',
                },
                {
                    file: testStructureSet.dicomStructureSet.getAsDicomBlob(),
                    filename: 'user',
                },
            ];

            const blobsToUpload = pseudonymize
                ? await Promise.all(
                      blobs.map(async (blob) => {
                          const result = await anonymizeBlobs(taskUID, [
                              blob.file,
                          ]);
                          return {
                              file: result![0],
                              filename: blob.filename,
                          };
                      })
                  )
                : blobs;

            if (blobsToUpload && gridImage) {
                upload(
                    blobsToUpload,
                    'ground_truth',
                    'user',
                    {
                        origin: [
                            ...gridImage.grid.getCornerVoxelCenterPosition(),
                        ],
                        direction: [
                            [...gridImage.grid.getUnitX()],
                            [...gridImage.grid.getUnitY()],
                            [...gridImage.grid.getUnitZ()],
                        ],
                        spacing: [...gridImage.grid.getRes()],
                        size: [...gridImage.grid.getSize()],
                    },
                    sessionClient
                );
            } else {
                handleError(undefined, FILES_FAILED_MESSAGE);
                fail(FILES_FAILED_MESSAGE);
            }
        } catch (error) {
            handleError(PROCESSING_FAILED_MESSAGE);
            fail(PROCESSING_FAILED_MESSAGE);
        }
    };

    return { submit, loading, data };
}
